/**
 * Liga de pontos corridos (item 5.3; GDD, seção 7): todos contra todos em turno e returno, 3 pontos por vitória e
 * 1 por empate. Com 6 clubes, são as 10 partidas por temporada do GDD.
 */
export const LEAGUE = {
  clubs: 6,
  points: { vitoria: 3, empate: 1, derrota: 0 },
} as const;

export interface Fixture {
  round: number;
  home: number;
  away: number;
}

/**
 * Tabela de jogos pelo método do círculo: o clube 0 fica parado e os outros giram. Turno e returno, com mando
 * invertido no returno; cada clube joga uma vez por rodada.
 */
export function roundRobin(n: number): Fixture[] {
  if (n < 2 || n % 2 !== 0) throw new Error("A liga precisa de um número par de clubes");
  const ids = Array.from({ length: n }, (_, i) => i);
  const homes = new Array<number>(n).fill(0);
  const first: Fixture[] = [];
  for (let r = 0; r < n - 1; r++) {
    for (let i = 0; i < n / 2; i++) {
      const a = ids[i] ?? 0;
      const b = ids[n - 1 - i] ?? 0;
      // Mando para quem jogou menos em casa até aqui (empate: o primeiro da dupla), para equilibrar o turno.
      const aHome = (homes[a] ?? 0) <= (homes[b] ?? 0);
      const home = aHome ? a : b;
      homes[home] = (homes[home] ?? 0) + 1;
      first.push({ round: r, home, away: aHome ? b : a });
    }
    // Gira todos menos o primeiro.
    const last = ids.pop();
    if (last !== undefined) ids.splice(1, 0, last);
  }
  const second = first.map((f) => ({ round: f.round + n - 1, home: f.away, away: f.home }));
  return [...first, ...second];
}

export interface PlayedResult {
  home: number;
  away: number;
  /** Pontos do placar (gols × 10, Faísca 30). */
  homeScore: number;
  awayScore: number;
}

export interface StandingRow {
  club: number;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  scored: number;
  conceded: number;
  points: number;
}

/** Classificação: pontos, depois saldo de placar, depois placar marcado, depois a ordem dos clubes. */
export function standings(clubCount: number, results: readonly PlayedResult[]): StandingRow[] {
  const rows: StandingRow[] = Array.from({ length: clubCount }, (_, club) => ({
    club,
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    scored: 0,
    conceded: 0,
    points: 0,
  }));
  for (const r of results) {
    const h = rows[r.home];
    const a = rows[r.away];
    if (!h || !a) continue;
    h.played++;
    a.played++;
    h.scored += r.homeScore;
    h.conceded += r.awayScore;
    a.scored += r.awayScore;
    a.conceded += r.homeScore;
    if (r.homeScore > r.awayScore) {
      h.won++;
      a.lost++;
    } else if (r.homeScore < r.awayScore) {
      a.won++;
      h.lost++;
    } else {
      h.drawn++;
      a.drawn++;
    }
  }
  for (const row of rows)
    row.points =
      row.won * LEAGUE.points.vitoria + row.drawn * LEAGUE.points.empate + row.lost * LEAGUE.points.derrota;
  return rows.sort(
    (x, y) =>
      y.points - x.points ||
      y.scored - y.conceded - (x.scored - x.conceded) ||
      y.scored - x.scored ||
      x.club - y.club,
  );
}
